import * as React from 'react';
import classnames from 'classnames';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';

import OfferBannerDetailsBudget from './budget/OfferBannerDetailsBudget';
import OfferBannerDetailsOtherArrangement from './other-arrangement/OfferBannerDetailsOtherArrangement';
import OfferBannerDetailsAdditionalCompensationVoucher from './additional-compensation/voucher/OfferBannerDetailsAdditionalCompensationVoucher';
import OfferBannerDetailsAdditionalCompensationOther from './additional-compensation/other/OfferBannerDetailsAdditionalCompensationOther';
import OfferBannerAdditionalCompensationDescriptions from '../additional-compensation/descriptions/OfferBannerAdditionalCompensationDescriptions';

import { DEFAULT } from '../utils/offer-banner';

import './offer-banner-details.scss';

import type { SubcampaignOffer } from 'types';
import type { OfferBannerTextStyle } from '../types';
import type {
  OfferAdditionalCompensationData
} from 'modules/subcampaigns/components/forms/offer-additional-compensation/types';

type Props = {
  offer: SubcampaignOffer | undefined,
  budgetLabel?: string,
  textStyle?: OfferBannerTextStyle,
  isCondensed?: boolean,
  showAdditionalCompensationDescriptions?: boolean
};

const OfferBannerDetails: React.FC<Props> = ({
  offer,
  budgetLabel,
  textStyle = DEFAULT,
  isCondensed = false,
  showAdditionalCompensationDescriptions
}: Props) => {
  const offerType = offer?.type || 'offer';
  const additionalCompensation = get(offer, 'additionalCompensation', {}) as OfferAdditionalCompensationData;

  const renderBudget = (): React.ReactNode => {
    if (offerType !== 'offer') {
      return null;
    }

    return (
      <div className="offer-banner-details__budget">
        <OfferBannerDetailsBudget
          label={budgetLabel || ''}
          amount={offer?.budget || 0}
        />
      </div>
    );
  };

  const renderAdditionalCompensationVoucher = (): React.ReactNode => {
    const amount = additionalCompensation?.voucherAmount;

    if (offerType !== 'offer' || isNil(amount)) {
      return null;
    }

    return (
      <div className="offer-banner-details__voucher">
        <OfferBannerDetailsAdditionalCompensationVoucher
          amount={amount}
        />
      </div>
    );
  };

  const renderAdditionalCompensationOther = (): React.ReactNode => {
    if (offerType !== 'offer' || isEmpty(additionalCompensation?.otherDescription)) {
      return null;
    }

    return (
      <div className="offer-banner-details__other">
        <OfferBannerDetailsAdditionalCompensationOther />
      </div>
    );
  };

  const renderOtherArrangement = (): React.ReactNode => {
    if (offerType !== 'other') {
      return null;
    }

    return (
      <OfferBannerDetailsOtherArrangement
        perks={offer?.perks}
        offerState={offer?.state}
      />
    );
  };

  const renderDetails = (): React.ReactNode => {
    const valueClasses = classnames(
      'offer-banner-details__values', {
        [`offer-banner-details__values--${textStyle}`]: true
      }
    );

    return (
      <div className={valueClasses}>
        {renderBudget()}
        {renderAdditionalCompensationVoucher()}
        {renderAdditionalCompensationOther()}
        {renderOtherArrangement()}
      </div>
    );
  };

  const containerClasses = classnames(
    'offer-banner-details', {
      'offer-banner-details--condensed': isCondensed
    }
  );

  return (
    <div className={containerClasses}>
      {renderDetails()}
      {
        showAdditionalCompensationDescriptions && (
          <OfferBannerAdditionalCompensationDescriptions
            additionalCompensation={additionalCompensation}
            textStyle={textStyle}
          />
        )
      }
    </div>
  );
};

export default OfferBannerDetails;
